import { inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { TagsState } from "@vp/data-access/tags";
import {
  AdvancedCommand,
  AdvancedCommandRequest,
  DeviceStethoscope,
  PatientConsoleDeviceData,
  SirenData,
  Tag,
  User
} from "@vp/models";
import { FeatureService } from "@vp/shared/features";
import { deepCopy, mergeDeep, propertyAtPath } from "@vp/shared/utilities";
import { AdvancedCommandsService } from "@vp/virtual-care/data-access";
import { createPatch, Operation } from "rfc6902";
import { EMPTY, map, Observable, of } from "rxjs";
import { UserApiService } from "../api/user-api.service";

export const defaultDeviceConfigurationSettings: Record<string, string> = {
  dimmedDisplayStartTime: "19:00",
  dimmedDisplayEndTime: "07:00",
  dimmedDisplayBrightness: "5",
  dimmedDisplayIntensity: "5",
  minimumVolume: "0.5",
  defaultVolume: "0.6",
  logFileUploadEnabled: "true",
  alertFrequencyInSeconds: "4000",
  lowBatteryAlertVolume: "50",
  minimumBatteryLevel: "25",
  quietHoursStartTime: "19:00",
  quietHoursEndTime: "07:00",
  patientMessageFontColor: "#32CD32",
  messageCenterDefaultFontSize: "50",

  resetCameraPositionAfterCall: "true",
  messageCenterEnabled: "true"
};

const VIRTUAL_CARE_FEATURE_ID = "virtualCare";
const VIRTUAL_CARE_DEFAULT_SETTINGS = "defaultSettings";

@Injectable({
  providedIn: "root"
})
export class DeviceService {
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);

  constructor(
    private readonly featureService: FeatureService,
    private readonly advancedCommandsService: AdvancedCommandsService,
    private readonly userApiService: UserApiService
  ) {}

  defaultDeviceSettings$ = this.featureService
    .patientConsoleSettingsValue$(VIRTUAL_CARE_FEATURE_ID, VIRTUAL_CARE_DEFAULT_SETTINGS)
    .pipe(map(defaultDeviceSettings => defaultDeviceSettings.settings as Record<string, string>));

  advancedCommands$ = this.advancedCommandsService.getDeviceCommands();

  getInteractiveChannelProviderType$ = (device: User | null) => {
    return this.tags$.pipe(
      map(tags => {
        let providerType = "zoom"; //default is zoom
        if (!device) return providerType;

        const facilityTag = tags.find(
          tag => device.assignedTags.includes(tag.tagId) && tag.tagTypeFriendlyId === "facility"
        );
        const facilityProviderType: string | undefined = propertyAtPath(
          facilityTag?.tagData,
          `services.videoConferencingService`
        );

        //override at the facility level
        if (
          facilityProviderType &&
          tags
            .find(tag => tag.tagId === facilityProviderType)
            ?.displayName?.toLowerCase()
            .startsWith("pexip")
        ) {
          providerType = "pexip";
        }

        //override at the device level
        providerType =
          device.patientConsoleDeviceData?.applicationSettings?.[
            "interactiveChannelProviderType"
          ] ?? providerType;

        return providerType.toLowerCase();
      })
    );
  };

  getDeviceSetting$ = (settingName: string, device: User | null) => {
    return this.deviceConfigurationSettings$(device).pipe(
      map(settings => {
        const settingValue = settings[settingName];
        return settingValue;
      })
    );
  };

  /**
   * Includes default device settings from the org, and any overrides from the device
   * @param device
   * @returns
   */
  deviceConfigurationSettings$ = (device: User | null) => {
    return this.defaultDeviceSettings$.pipe(
      map(defaultSetting => {
        const deviceSetting =
          device?.patientConsoleDeviceData?.applicationSettings ?? ({} as Record<string, string>);
        const mergedSetting = mergeDeep(defaultSetting, deviceSetting);
        return mergedSetting ?? defaultDeviceConfigurationSettings;
      })
    );
  };

  toggleDeviceSiren(device: User) {
    const clone = deepCopy(device) as User;
    clone.patientConsoleDeviceData = {
      ...(device.patientConsoleDeviceData || {}),
      sirenData: {
        ...(device?.patientConsoleDeviceData?.sirenData || {}),
        sirenStatus: !device?.patientConsoleDeviceData?.sirenData?.sirenStatus
      } as SirenData
    } as PatientConsoleDeviceData;
    const operations = createPatch(device, clone);
    return this.userApiService.patch(device.userId, operations);
  }

  patchDeviceAppSettings(device: User, deviceAppSettings: Record<string, string>) {
    const updated = deepCopy(device) as User;

    // Initialize patientConsoleDeviceData if it doesn't exist
    if (!updated.patientConsoleDeviceData) {
      updated.patientConsoleDeviceData = {
        applicationSettings: {}
      } as any;
    } else if (!updated.patientConsoleDeviceData.applicationSettings) {
      // Initialize applicationSettings if it doesn't exist
      updated.patientConsoleDeviceData.applicationSettings = {};
    }

    // Merge the new settings - at this point patientConsoleDeviceData is guaranteed to exist
    updated.patientConsoleDeviceData!.applicationSettings = {
      ...updated.patientConsoleDeviceData!.applicationSettings,
      ...deviceAppSettings
    };

    const operations = createPatch(device, updated);

    if (operations.length === 0) {
      return of(false);
    }
    return this.userApiService.patch(device.userId, operations).pipe(map(() => true));
  }

  sendAdvancedCommand(device: User, command: AdvancedCommand) {
    const sendCommand: AdvancedCommandRequest = {
      commandName: command.methodName,
      deviceUserId: device.userId,
      commandValue: command.commandValue
    } as AdvancedCommandRequest;

    return this.advancedCommandsService.sendDeviceCommand(sendCommand);
  }

  startedEkoStreaming(user: User, device: User, ekoStethoscope: DeviceStethoscope) {
    const index = device?.patientConsoleDeviceData?.deviceStethoscopes?.findIndex(
      eko => eko.deviceAddress === ekoStethoscope.deviceAddress
    );

    if (index === undefined || index < 0) return EMPTY;

    const operations: Operation[] = [
      {
        op: "replace",
        path: `/patientConsoleDeviceData/deviceStethoscopes/${index}/lastStreamingUser`,
        value: user.email
      }
    ];

    return this.userApiService.patch(device.userId, operations);
  }

  buildFacilityHubDisplayName(deviceAssignedTags: string[], allTags: Tag[]) {
    const assignedTags = allTags.filter(t => deviceAssignedTags.includes(t.tagId));
    const facilityTag = assignedTags.filter(t => t.tagTypeFriendlyId === "facility");
    const hubTag = assignedTags.filter(t => t.tagTypeFriendlyId === "hub");
    return facilityTag
      .concat(hubTag)
      .map(t => t.displayName)
      .join("/");
  }
}
